/**
 * Single source of truth for Material Pickup pipeline stage labels,
 * chip variants, and allowed transitions.
 *
 * Mirrors the Python constants in
 * `middleware/app/models/material_pickup.py`. Any drift between
 * frontend and backend trips a unit test.
 */

import type { Stage } from '../api/types'

type ChipVariant = 'neutral' | 'success' | 'warning' | 'danger' | 'info'

/** Board display order — left to right on the kanban. */
export const STAGES: readonly Stage[] = [
  'new_request',
  'needs_info',
  'vendor_contacted',
  'pickup_scheduled',
  'in_transit',
  'received',
  'pickup_cancelled',
] as const

export const TERMINAL_STAGES: readonly Stage[] = ['received', 'pickup_cancelled'] as const

/** Non-terminal stages — shown as kanban columns and drag targets. */
export const BOARD_STAGES: readonly Stage[] = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
) as readonly Stage[]

/** Full labels for detail views, history timeline, and filter dropdowns. */
export const STAGE_LABELS: Record<Stage, string> = {
  new_request: 'New Request',
  needs_info: 'Needs Info',
  vendor_contacted: 'Vendor Contacted',
  pickup_scheduled: 'Pickup Scheduled',
  in_transit: 'In Transit',
  received: 'Received',
  pickup_cancelled: 'Cancelled',
}

/** Short labels for kanban column headers. */
export const STAGE_SHORT_LABELS: Record<Stage, string> = {
  new_request: 'New',
  needs_info: 'Needs Info',
  vendor_contacted: 'Contacted',
  pickup_scheduled: 'Scheduled',
  in_transit: 'In Transit',
  received: 'Received',
  pickup_cancelled: 'Cancelled',
}

/** Chip accent colors. */
export const STAGE_VARIANT: Record<Stage, ChipVariant> = {
  new_request: 'neutral',
  needs_info: 'warning',
  vendor_contacted: 'info',
  pickup_scheduled: 'info',
  in_transit: 'info',
  received: 'success',
  pickup_cancelled: 'danger',
}

/** KanbanBoard stage accent (neutral|success|warning|danger). */
export const STAGE_KANBAN_ACCENT: Record<
  Stage,
  'neutral' | 'success' | 'warning' | 'danger'
> = {
  new_request: 'neutral',
  needs_info: 'warning',
  vendor_contacted: 'neutral',
  pickup_scheduled: 'neutral',
  in_transit: 'neutral',
  received: 'success',
  pickup_cancelled: 'danger',
}

// ─── Transitions (permissive kanban D-5 rule) ─────────────────────
// Mirrors ALLOWED_TRANSITIONS in Python model:
//   Any non-terminal → any other stage (incl. terminals)
//   Terminals can only undo to needs_info

type Transition = { from: Stage; to: Stage }

const _nonTerminals = STAGES.filter(
  (s) => !TERMINAL_STAGES.includes(s as (typeof TERMINAL_STAGES)[number]),
)

export const ALLOWED_TRANSITIONS: readonly Transition[] = [
  // Non-terminal can go to any other stage
  ..._nonTerminals.flatMap((from) =>
    STAGES.filter((to) => to !== from).map((to) => ({ from, to })),
  ),
  // Terminals can only undo to needs_info
  { from: 'received', to: 'needs_info' },
  { from: 'pickup_cancelled', to: 'needs_info' },
]

export function transitionAllowed(from: Stage, to: Stage): boolean {
  if (from === to) return false
  return ALLOWED_TRANSITIONS.some((t) => t.from === from && t.to === to)
}

export function allowedTargetsFrom(stage: Stage): Stage[] {
  return ALLOWED_TRANSITIONS.filter((t) => t.from === stage).map((t) => t.to)
}

export function isTerminal(stage: Stage): boolean {
  return TERMINAL_STAGES.includes(stage as (typeof TERMINAL_STAGES)[number])
}

/** True for synthetic extraction sub-state history entries. */
export function isExtractionMarker(stage: string): boolean {
  return stage.startsWith('extraction:')
}

/** True for synthetic duplicate-soft-delete history entries. */
export function isDuplicateMarker(stage: string): boolean {
  return stage.startsWith('duplicate:')
}
